import * as React from 'react';
import { useQuery } from '@tanstack/react-query';
import { Download } from 'lucide-react';
import { toast } from 'sonner';
import { api, type ReportBucketDto } from '@/lib/api';
import { Page } from '@/components/AppShell';
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  CardTitle,
  Field,
  Input,
  LoadingState,
  Stat,
} from '@/components/ui/primitives';
import { Tooltip } from '@/components/ui/overlays';
import { formatCents, formatDuration } from '@/lib/utils';

export function ReportsPage() {
  const [from, setFrom] = React.useState('');
  const [to, setTo] = React.useState('');

  const reports = useQuery({
    queryKey: ['reports', from, to],
    queryFn: () => api.reports(from || undefined, to || undefined),
  });

  const download = async (report: string) => {
    try {
      await api.exportReport(report, from || undefined, to || undefined);
      toast.success('Export downloaded');
    } catch (e) {
      toast.error((e as Error).message);
    }
  };

  if (reports.isLoading) return <Page title="Reports"><LoadingState /></Page>;
  const data = reports.data!;
  const effectiveness = data.callEffectiveness;

  return (
    <Page
      title="Reports"
      description="Every figure is counted from real records. Money is split into booked, collected and outstanding rather than one ambiguous total."
      actions={
        <div className="flex items-end gap-2">
          <Field label="From">
            <Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="h-7 w-36" />
          </Field>
          <Field label="To">
            <Input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="h-7 w-36" />
          </Field>
          {(from || to) && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                setFrom('');
                setTo('');
              }}
            >
              Clear
            </Button>
          )}
        </div>
      }
    >
      <div className="space-y-6 p-5 md:p-6">
        <section>
          <h2 className="section-label mb-2">Money</h2>
          <div className="grid grid-cols-1 gap-3 md:grid-cols-4">
            <Stat label="Booked sales" value={formatCents(data.money.bookedSalesCents)} hint={`${data.money.saleCount} sales`} />
            <Stat
              label="Collected payments"
              value={formatCents(data.money.collectedPaymentsCents)}
              valueClassName="text-emerald-300"
              hint="Net of refunds"
            />
            <Stat
              label="Outstanding balance"
              value={formatCents(data.money.outstandingBalanceCents)}
              valueClassName={data.money.outstandingBalanceCents > 0 ? 'text-amber-300' : undefined}
              hint="Across all customers"
            />
            <Stat label="Average sale" value={formatCents(data.money.saleCount ? Math.round(data.money.bookedSalesCents / data.money.saleCount) : 0)} />
          </div>
        </section>

        <section>
          <h2 className="section-label mb-2">Calling effectiveness</h2>
          <div className="grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-6">
            <Stat label="Call attempts" value={effectiveness.totalAttempts.toLocaleString()} />
            <Stat
              label="Contact rate"
              value={`${Math.round(effectiveness.contactRate * 100)}%`}
              hint={`${effectiveness.contactedAttempts} contacting calls`}
            />
            <Stat
              label="Interest rate"
              value={`${Math.round(effectiveness.interestRate * 100)}%`}
              hint={`${effectiveness.leadsInterested} of ${effectiveness.leadsContacted} contacted`}
            />
            <Stat
              label="Attempts before contact"
              value={effectiveness.averageAttemptsBeforeContact ?? '—'}
              hint="Average, per lead reached"
            />
            <Stat label="Leads attempted" value={effectiveness.leadsAttempted.toLocaleString()} />
            <Tooltip content="Derived from a tel:/sip: handoff, so this is approximate by nature">
              <div>
                <Stat
                  label="Approx. talk time"
                  value={formatDuration(effectiveness.approximateTalkTimeSeconds)}
                  hint="Approximate"
                />
              </div>
            </Tooltip>
          </div>
        </section>

        <div className="grid grid-cols-1 gap-3 xl:grid-cols-2">
          <BucketCard title="New leads by date" report="newLeadsByDate" buckets={data.newLeadsByDate} onExport={download} />
          <BucketCard title="Calls by date" report="callsByDate" buckets={data.callsByDate} onExport={download} />
          <BucketCard title="Calls by outcome" report="callsByOutcome" buckets={data.callsByOutcome} onExport={download} />
          <BucketCard title="Pipeline conversion" report="pipelineConversion" buckets={data.pipelineConversion} onExport={download} />
          <BucketCard title="Leads by category" report="leadsByCategory" buckets={data.leadsByCategory} onExport={download} />
          <BucketCard title="Leads by county" report="leadsByCounty" buckets={data.leadsByCounty} onExport={download} />
          {/* Coverage, not location: a lead serving three counties counts in all three. */}
          <BucketCard
            title="Counties served"
            report="leadsByServiceCounty"
            buckets={data.leadsByServiceCounty}
            onExport={download}
            note="Coverage, not location. A lead is counted once in every county it serves, so these numbers add up to more than the number of leads."
          />
          <BucketCard title="Leads by city" report="leadsByCity" buckets={data.leadsByCity} onExport={download} />
          <BucketCard title="Leads by web presence" report="leadsByWebPresence" buckets={data.leadsByWebPresence} onExport={download} />
          <BucketCard
            title="Leads by website condition"
            report="leadsByWebsiteCondition"
            buckets={data.leadsByWebsiteCondition}
            onExport={download}
          />
          <BucketCard title="Leads by website issue" report="leadsByWebsiteIssue" buckets={data.leadsByWebsiteIssue} onExport={download} />
          <BucketCard title="Leads by issue severity" report="leadsByIssueSeverity" buckets={data.leadsByIssueSeverity} onExport={download} />
          <BucketCard title="Leads by score band" report="leadsByScoreBand" buckets={data.leadsByScoreBand} onExport={download} />
          <BucketCard title="Source performance" report="sourcePerformance" buckets={data.sourcePerformance} onExport={download} />
          <BucketCard title="Import success and failure" report="importOutcomes" buckets={data.importOutcomes} onExport={download} />
          <BucketCard title="Duplicates prevented" report="duplicatesPrevented" buckets={data.duplicatesPrevented} onExport={download} />
        </div>
      </div>
    </Page>
  );
}

function BucketCard({
  title,
  report,
  buckets,
  onExport,
  note,
}: {
  title: string;
  report: string;
  buckets: ReportBucketDto[];
  onExport: (report: string) => void;
  /** One line under the title, for a report whose totals need explaining. */
  note?: string;
}) {
  const max = Math.max(1, ...buckets.map((b) => b.count));
  const extraKeys = [...new Set(buckets.flatMap((b) => Object.keys(b.extra ?? {})))];

  return (
    <Card>
      <CardHeader>
        <CardTitle>{title}</CardTitle>
        <Button variant="ghost" size="xs" onClick={() => onExport(report)}>
          <Download className="h-3 w-3" />
          CSV
        </Button>
      </CardHeader>
      <CardBody>
        {note && <p className="mb-2 text-xs text-muted-foreground">{note}</p>}
        {buckets.length === 0 ? (
          <p className="text-xs text-muted-foreground">No data in this range.</p>
        ) : (
          <div className="max-h-72 space-y-1 overflow-y-auto">
            {buckets.slice(0, 40).map((bucket) => (
              <div key={bucket.key} className="flex items-center gap-2 text-xs">
                <span className="w-40 shrink-0 truncate text-muted-foreground" title={bucket.label}>
                  {bucket.label}
                </span>
                <div className="h-2.5 min-w-0 flex-1 overflow-hidden rounded-full bg-muted">
                  <div
                    className="h-full bg-primary/70"
                    style={{ width: `${Math.max(2, (bucket.count / max) * 100)}%` }}
                  />
                </div>
                <span className="w-12 shrink-0 text-right font-medium tabular-nums">
                  {bucket.count.toLocaleString()}
                </span>
                {extraKeys.length > 0 && (
                  <span className="w-24 shrink-0 truncate text-right text-2xs text-muted-foreground">
                    {extraKeys
                      .map((k) => (bucket.extra?.[k] !== undefined ? `${k}: ${bucket.extra[k]}` : null))
                      .filter(Boolean)
                      .join(' · ')}
                  </span>
                )}
              </div>
            ))}
          </div>
        )}
      </CardBody>
    </Card>
  );
}
